import { useState } from "react";
import { Plus, Trash2, Pencil, Server, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/Layout";
import { SyncDialog } from "@/components/SyncDialog";
import { useAccounts } from "@/hooks/useAccounts";
import { api, errMsg } from "@/lib/api";
import { ACCOUNT_TYPES, fmtPnl, pnlClass } from "@/lib/trading";

const EMPTY = { broker: "", account_number: "", server: "", platform: "MT5", account_type: "live", initial_balance: "", currency: "USD" };
const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs text-slate-400">{label}</Label>{children}</div>;

const AccountDialog = ({ open, onOpenChange, account, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [prev, setPrev] = useState(null);
  if (open && prev !== (account || "new")) { setPrev(account || "new"); setF(account ? { ...account } : EMPTY); }
  if (!open && prev) setPrev(null);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    if (!f.broker || !f.account_number) return toast.error("Broker and account number are required");
    const body = { ...f, initial_balance: Number(f.initial_balance || 0) };
    try {
      if (account) await api.put(`/accounts/${account.id}`, body); else await api.post("/accounts", body);
      toast.success(account ? "Account updated" : "Account added");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121722] border-slate-800" data-testid="account-dialog">
        <DialogHeader><DialogTitle className="font-display">{account ? "Edit account" : "Add broker account"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Broker"><Input data-testid="account-broker-input" value={f.broker} onChange={set("broker")} placeholder="IC Markets, FTMO…" /></Field>
          <Field label="Account number"><Input data-testid="account-number-input" value={f.account_number} onChange={set("account_number")} className="font-mono" /></Field>
          <Field label="Server"><Input data-testid="account-server-input" value={f.server} onChange={set("server")} placeholder="ICMarkets-Live01" /></Field>
          <Field label="Platform">
            <Select value={f.platform} onValueChange={set("platform")}>
              <SelectTrigger data-testid="account-platform-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["MT4", "MT5", "cTrader", "TradingView", "Other"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Account type">
            <Select value={f.account_type} onValueChange={set("account_type")}>
              <SelectTrigger data-testid="account-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ACCOUNT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Currency"><Input data-testid="account-currency-input" value={f.currency} onChange={set("currency")} className="font-mono uppercase" /></Field>
          <div className="col-span-2"><Field label="Starting balance"><Input data-testid="account-balance-input" type="number" value={f.initial_balance} onChange={set("initial_balance")} className="font-mono" /></Field></div>
        </div>
        <Button onClick={save} data-testid="save-account-button" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">Save account</Button>
      </DialogContent>
    </Dialog>
  );
};

const AccountCard = ({ a, onEdit, onDelete, onSync, i }) => {
  const t = ACCOUNT_TYPES[a.account_type];
  return (
    <div className="panel p-5 rise hover:border-slate-600 transition-colors" style={{ animationDelay: `${i * 60}ms` }} data-testid={`account-card-${a.id}`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="font-display font-semibold text-lg">{a.broker}</p>
          <p className="font-mono text-xs text-slate-500">#{a.account_number} · {a.platform}</p>
        </div>
        <span className={`text-[11px] font-mono px-2 py-0.5 rounded border ${t.badge}`} data-testid={`account-type-badge-${a.id}`}>{t.label}</span>
      </div>
      {a.server && <p className="mt-3 text-xs text-slate-500 flex items-center gap-1"><Server className="h-3 w-3" />{a.server}</p>}
      <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-800">
        <div><p className="text-[11px] font-mono uppercase text-slate-500">Balance</p><p className="font-mono font-semibold" data-testid={`account-balance-${a.id}`}>{a.balance.toLocaleString("en-US", { minimumFractionDigits: 2 })} {a.currency}</p></div>
        <div><p className="text-[11px] font-mono uppercase text-slate-500">Net P&L</p><p className={`font-mono font-semibold ${pnlClass(a.total_pnl)}`}>{fmtPnl(a.total_pnl)}</p></div>
      </div>
      <div className="flex justify-between items-center mt-4">
        <span className="text-xs font-mono text-slate-500">{a.trade_count} trades</span>
        <div className="flex items-center">
          <button onClick={() => onSync(a)} data-testid={`sync-account-${a.id}`} className={`mr-1 flex items-center gap-1 px-2 py-1 rounded border text-xs font-mono transition-colors ${a.last_sync_at ? "border-emerald-800/50 text-emerald-400" : "border-slate-700 text-slate-400 hover:text-slate-100"}`}>
            <RefreshCcw className="h-3 w-3" />{a.last_sync_at ? "Synced" : "Auto-sync"}
          </button>
          <button onClick={() => onEdit(a)} data-testid={`edit-account-${a.id}`} className="p-1.5 text-slate-500 hover:text-slate-200"><Pencil className="h-4 w-4" /></button>
          <button onClick={() => onDelete(a)} data-testid={`delete-account-${a.id}`} className="p-1.5 text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};

export default function Accounts() {
  const { accounts, reload } = useAccounts();
  const [dialog, setDialog] = useState({ open: false, account: null });
  const [syncAcc, setSyncAcc] = useState(null);
  const del = async (a) => {
    if (!window.confirm(`Delete ${a.broker} #${a.account_number}? All its trades and copy links will be removed.`)) return;
    try { await api.delete(`/accounts/${a.id}`); toast.success("Account deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageHeader title="Broker Accounts" subtitle="Add every live, demo or prop firm account you trade.">
        <Button onClick={() => setDialog({ open: true, account: null })} data-testid="add-account-button" className="bg-emerald-600 hover:bg-emerald-500 text-white"><Plus className="h-4 w-4 mr-1" />Add account</Button>
      </PageHeader>
      {!accounts.length && <p className="panel p-10 text-center text-slate-500 text-sm" data-testid="accounts-empty">No accounts yet.</p>}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {accounts.map((a, i) => <AccountCard key={a.id} a={a} i={i} onEdit={(x) => setDialog({ open: true, account: x })} onDelete={del} onSync={setSyncAcc} />)}
      </div>
      <SyncDialog account={syncAcc} onClose={() => setSyncAcc(null)} onChanged={reload} />
      <AccountDialog open={dialog.open} account={dialog.account} onOpenChange={(o) => setDialog({ ...dialog, open: o })} onSaved={reload} />
    </>
  );
}
